import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, BadgeCheck, Paperclip, X, Send, Loader2 } from "lucide-react";
import { COMPANY } from "../data/site";
import { FadeUp, SectionHead } from "./motion";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nom: "", entreprise: "", telephone: "", email: "", localite: "", type_travaux: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-sky-500 focus:ring-4 focus:ring-sky-100";

export const DevisForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const { S, lang } = useLang();
  const D = S.devis;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const valid = incoming.filter((f) => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(D.fileTooBigTitle, { description: `${f.name} ${D.fileTooBigSuffix}` });
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...valid].slice(0, 6));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("lang", lang);
      files.forEach((f) => fd.append("fichiers", f));
      await axios.post(`${API}/devis`, fd);
      toast.success(D.toastOkTitle, { description: D.toastOkDesc });
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(D.toastErrTitle, {
        description: typeof detail === "string" ? detail : D.toastErrDesc,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="devis" data-testid="devis-section" className="relative bg-white py-24 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow={D.eyebrow}
          title={D.title}
          subtitle={D.subtitle}
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <FadeUp className="lg:col-span-3">
            <form
              data-testid="devis-form"
              onSubmit={onSubmit}
              className="rounded-3xl border border-slate-200 bg-slate-50 p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="devis-nom" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {D.labels.nom}
                  </label>
                  <input id="devis-nom" data-testid="devis-input-nom" required value={form.nom} onChange={set("nom")} className={inputCls} placeholder={D.placeholders.nom} />
                </div>
                <div>
                  <label htmlFor="devis-entreprise" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {D.labels.entreprise} <span className="normal-case font-normal text-slate-400">{D.labels.optional}</span>
                  </label>
                  <input id="devis-entreprise" data-testid="devis-input-entreprise" value={form.entreprise} onChange={set("entreprise")} className={inputCls} placeholder={D.placeholders.entreprise} />
                </div>
                <div>
                  <label htmlFor="devis-telephone" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {D.labels.telephone}
                  </label>
                  <input id="devis-telephone" data-testid="devis-input-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} className={inputCls} placeholder={D.placeholders.telephone} />
                </div>
                <div>
                  <label htmlFor="devis-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {D.labels.email}
                  </label>
                  <input id="devis-email" data-testid="devis-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={D.placeholders.email} />
                </div>
                <div>
                  <label htmlFor="devis-localite" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {D.labels.localite}
                  </label>
                  <input id="devis-localite" data-testid="devis-input-localite" required value={form.localite} onChange={set("localite")} className={inputCls} placeholder={D.placeholders.localite} />
                </div>
                <div>
                  <label htmlFor="devis-type" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {D.labels.type}
                  </label>
                  <select id="devis-type" data-testid="devis-input-type" required value={form.type_travaux} onChange={set("type_travaux")} className={inputCls}>
                    <option value="" disabled>
                      {D.placeholders.select}
                    </option>
                    {D.types.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="devis-message" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {D.labels.message}
                </label>
                <textarea
                  id="devis-message"
                  data-testid="devis-input-message"
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  className={`${inputCls} resize-none`}
                  placeholder={D.placeholders.message}
                />
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {D.labels.files} <span className="normal-case font-normal text-slate-400">{D.labels.filesHint}</span>
                </label>
                <label
                  data-testid="devis-file-dropzone"
                  className="flex cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-white px-6 py-8 text-sm text-slate-500 transition-all duration-300 hover:border-sky-400 hover:bg-sky-50/50"
                >
                  <Paperclip className="w-5 h-5 text-sky-600" />
                  {D.dropzone}
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    className="hidden"
                    data-testid="devis-file-input"
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
                {files.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2" data-testid="devis-file-list">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs text-slate-600">
                        {f.name}
                        <button
                          type="button"
                          data-testid={`devis-file-remove-${i}`}
                          onClick={() => setFiles(files.filter((_, j) => j !== i))}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          aria-label={`Retirer ${f.name}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                data-testid="devis-submit-button"
                disabled={sending}
                className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-sky-600 px-10 py-4 text-sm font-semibold text-white shadow-xl shadow-sky-600/25 transition-all duration-300 hover:bg-sky-500 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? D.sending : D.submit}
              </button>
              <p className="mt-4 text-xs text-slate-400">{D.privacy}</p>
            </form>
          </FadeUp>

          <FadeUp delay={0.12} className="lg:col-span-2">
            <aside id="contact" data-testid="contact-card" className="relative h-full overflow-hidden rounded-3xl bg-[#0A182E] p-8 lg:p-10 grain">
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-sky-600/15 blur-3xl" aria-hidden="true" />
              <h3 className="relative font-display text-xl font-bold text-white">{D.contact.title}</h3>
              <p className="relative mt-2 text-sm text-slate-300">{D.contact.intro}</p>

              <div className="relative mt-8 space-y-5">
                {COMPANY.phones.map((p, i) => (
                  <a
                    key={p.href}
                    href={p.href}
                    data-testid={`contact-phone-${i + 1}`}
                    className="flex items-center gap-4 text-white group"
                  >
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-sky-600/20 text-sky-400 border border-sky-500/20 transition-colors duration-300 group-hover:bg-sky-600 group-hover:text-white">
                      <Phone className="w-5 h-5" />
                    </span>
                    <span className="text-base font-semibold tracking-wide">{p.label}</span>
                  </a>
                ))}
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 text-white group">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-sky-600/20 text-sky-400 border border-sky-500/20 transition-colors duration-300 group-hover:bg-sky-600 group-hover:text-white">
                    <Mail className="w-5 h-5" />
                  </span>
                  <span className="text-base font-semibold">{COMPANY.email}</span>
                </a>
                <div className="flex items-center gap-4 text-slate-200">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/5 text-sky-400 border border-white/10">
                    <MapPin className="w-5 h-5" />
                  </span>
                  <span className="text-sm leading-relaxed">
                    GPN Sàrl — {COMPANY.tagline}
                    <br />
                    {COMPANY.address}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-slate-200">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/5 text-sky-400 border border-white/10">
                    <Clock className="w-5 h-5" />
                  </span>
                  <span className="text-sm">{D.contact.hours}</span>
                </div>
              </div>

              <div className="relative mt-10 rounded-2xl border border-sky-500/25 bg-sky-600/10 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-sky-300">
                  <BadgeCheck className="w-5 h-5" />
                  {D.contact.freeTitle}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate-300">{D.contact.freeText}</p>
              </div>
            </aside>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
